import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { SpawnChildrenBehavior, SpawnConfig } from './SpawnChildrenBehavior';

// Re-export SpawnConfig for backwards compatibility
export type { SpawnConfig } from './SpawnChildrenBehavior';

export interface TransformConfig extends BehaviorConfig {
  /** Distance at which player triggers transformation */
  detectionRange?: number;
  /** Animation name to play during transformation (e.g., "Transform") */
  transformAnimation?: string;
  /** Entities to spawn when transformation completes. Can be prefab names or SpawnConfig objects */
  spawnOnTransform?: (string | SpawnConfig)[];
}

/**
 * TransformBehavior - Handles proximity-triggered transformation and entity spawning
 *
 * Used by: Farm1 (transforms into cannon turret when player approaches)
 *
 * Features:
 * - Detects player proximity
 * - Plays transformation animation
 * - Spawns child entities via EntityFactory (reads from SSOT)
 * - Connects PinBehavior between spawned entities
 * - Cleans up children on destroy
 *
 * Config from SSOT (entities-overrides.json):
 * ```json
 * "Farm1": {
 *   "behaviors": {
 *     "transform": {
 *       "detectionRange": 500,
 *       "transformAnimation": "Transform",
 *       "spawnOnTransform": ["CannonBase", "CannonCannon"]
 *     }
 *   }
 * }
 * ```
 */
export class TransformBehavior extends Behavior {
  public readonly detectionRange: number;
  private transformAnimation: string | null;
  private spawnOnTransform: (string | SpawnConfig)[];
  private hasTransformed: boolean = false;
  /** Internal SpawnChildrenBehavior that handles actual spawning */
  private spawner: SpawnChildrenBehavior | null = null;

  constructor(entity: Entity, config: TransformConfig = {}) {
    super(entity, config);

    this.detectionRange = config.detectionRange ?? 500;
    this.transformAnimation = config.transformAnimation ?? null;
    this.spawnOnTransform = config.spawnOnTransform ?? [];
  }

  /**
   * Update - check for player proximity to trigger transformation
   */
  override update(_time: number, _delta: number): void {
    if (!this.enabled || this.hasTransformed) return;

    // Check if entity is dead (Structure/Unit have isDead())
    const entityWithDead = this.entity as Entity & { isDead?: () => boolean };
    if (entityWithDead.isDead?.()) return;

    // Get player from registry
    const player = this.scene.registry.get('player') as Entity | undefined;
    if (!player || !player.active) return;

    // Calculate distance
    const distance = Phaser.Math.Distance.Between(
      this.entity.x, this.entity.y,
      player.x, player.y
    );

    if (distance <= this.detectionRange) {
      this.startTransformation();
    }
  }

  /**
   * Start the transformation sequence
   */
  startTransformation(): void {
    if (this.hasTransformed) return;
    this.hasTransformed = true;

    // Set phase if entity supports it (Structure, Boss)
    const entityWithPhase = this.entity as Entity & { setPhase?: (phase: string) => void };
    if (typeof entityWithPhase.setPhase === 'function') {
      entityWithPhase.setPhase('transform');
    }

    // Play transformation animation
    if (this.transformAnimation) {
      const animKey = this.entity.getAnimationKey(this.transformAnimation);
      if (this.scene.anims.exists(animKey)) {
        this.entity.play(animKey);
        this.entity.once('animationcomplete', () => {
          this.onTransformComplete();
        });
        return;
      }
    }

    // No animation or not found - complete immediately
    this.onTransformComplete();
  }

  /**
   * Called when transformation animation completes
   * Delegates to SpawnChildrenBehavior for the actual spawning
   */
  private onTransformComplete(): void {
    // Set phase to default
    const entityWithPhase = this.entity as Entity & { setPhase?: (phase: string) => void };
    if (typeof entityWithPhase.setPhase === 'function') {
      entityWithPhase.setPhase('default');
    }

    // Normalize spawn configs (string → SpawnConfig)
    const normalizedConfigs: SpawnConfig[] = this.spawnOnTransform.map(spawnData =>
      typeof spawnData === 'string' ? { entityId: spawnData } : spawnData
    );

    // Create SpawnChildrenBehavior to handle spawning
    this.spawner = new SpawnChildrenBehavior(this.entity, {
      spawnChildren: normalizedConfigs,
    });

    // Trigger spawn
    this.spawner.spawn();

    // Emit event for external systems
    this.scene.events.emit('entityTransformed', {
      entity: this.entity,
      spawnedEntities: this.spawner.getSpawnedEntities(),
    });
  }

  /**
   * Cleanup when behavior is destroyed (parent entity dies)
   */
  override destroy(): void {
    if (this.spawner) {
      this.spawner.destroy();
      this.spawner = null;
    }
    super.destroy();
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Get all spawned entities
   */
  getSpawnedEntities(): Entity[] {
    return this.spawner?.getSpawnedEntities() ?? [];
  }

  /**
   * Check if transformation has completed
   */
  hasCompletedTransform(): boolean {
    return this.hasTransformed;
  }

  /**
   * Force transformation (for cutscenes or testing)
   */
  forceTransform(): void {
    if (!this.hasTransformed) {
      this.startTransformation();
    }
  }

  /**
   * Check if the cannon is active (transformed and has active children)
   */
  isTransformActive(): boolean {
    return this.hasTransformed && (this.spawner?.hasActiveChildren() ?? false);
  }

  /**
   * Get a spawned entity by index
   */
  getSpawnedAt(index: number): Entity | null {
    return this.spawner?.getSpawnedAt(index) ?? null;
  }
}
